import { useState, useEffect, useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'

interface OutletContext {
  currentStep: number
  goToStep: (index: number) => void
  handleForward: () => void
  handleBack: () => void
}

export default function NameScreen() {
  const { t } = useTranslation()
  const { handleForward } = useOutletContext<OutletContext>()
  const { user } = useAuth()
  const [displayName, setDisplayName] = useState('')
  const [visible, setVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!user || !displayName.trim()) return
    setSaving(true)
    setError(null)

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ display_name: displayName.trim() })
        .eq('id', user.id)

      if (updateError) throw updateError
      handleForward()
    } catch (err: unknown) {
      console.error('Onboarding name save failed:', err)
      const message = err instanceof Error ? err.message
        : (err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : t('onboarding.save_failed'))
      setError(message)
    } finally {
      setSaving(false)
    }
  }, [user, displayName, handleForward])

  return (
    <div className="flex flex-col py-8">
      {/* Title */}
      <h1
        className={`font-display text-3xl text-ink mb-2 transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {t('onboarding.name_title')}
      </h1>

      {/* Helper text */}
      <p
        className={`font-body text-ink-muted leading-relaxed mb-8 transition-all duration-700 delay-100 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {t('onboarding.name_subtitle')}
      </p>

      {/* Input */}
      <div
        className={`transition-all duration-700 delay-200 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        <Input
          type="text"
          placeholder={t('onboarding.name_placeholder')}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="w-full h-14 text-xl font-body text-center bg-transparent border-ink/10 focus:border-green focus:ring-green/20"
          maxLength={50}
          autoFocus
        />
      </div>

      {error && (
        <p className="mt-4 text-terra text-sm text-center font-body">{error}</p>
      )}

      {/* Submit */}
      <div
        className={`mt-8 transition-all duration-700 delay-300 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        <Button
          onClick={handleSubmit}
          disabled={!displayName.trim() || saving}
          className="w-full h-12 btn-primary"
        >
          {saving ? t('common.saving') : t('onboarding.next')}
        </Button>
      </div>
    </div>
  )
}
